// src/components/issues/ChampReference.tsx — SCE Issues
// Saisie d'une référence qualité : FNC-T_<année>-<numéro>, NC-S_<année>-<numéro>.
//
// ─── Pourquoi trois morceaux plutôt qu'un champ libre ───
//
// Le format est fixé par l'AQ (« FNC-T_2026-55 »), et c'est lui qui permettra
// un jour de retrouver le document dans le SharePoint qualité. Un champ libre
// laisserait passer « FNC-T 2026-55 », « fnct_2026-55 », « 2026-55 » : autant
// de références introuvables. Ici le préfixe est figé, l'année est pré-remplie
// (modifiable : une FNC de décembre peut se clore en janvier) et seul le
// numéro se tape — au clavier numérique, sur téléphone.
//
// Le numéro accepte 1 à 3 chiffres : il repart de 1 chaque année, sans zéro de
// tête. La normalisation finale (« 05 » → « 5 ») est faite par
// `composerReference()`, pas ici : ce composant ne fait que saisir.

import type { BadgeAvecReference } from '@/types/issue.types'

export interface SaisieReference {
  annee: string
  numero: string
}

export function ChampReference({
  badge,
  valeur,
  onChange,
  desactive,
  invalide,
  id,
}: {
  badge: BadgeAvecReference
  valeur: SaisieReference
  onChange: (v: SaisieReference) => void
  desactive?: boolean
  /** Contour renforcé quand la saisie est incomplète (année tronquée…). */
  invalide?: boolean
  /** Id du champ numéro, relié au <label> de l'appelant. */
  id: string
}): JSX.Element {
  const chiffres = (v: string, max: number): string => v.replace(/\D/g, '').slice(0, max)

  const champ = {
    height: '100%',
    boxSizing: 'border-box',
    border: 'none',
    background: 'transparent',
    fontSize: 'var(--font-size-copy2)',
    color: 'var(--color-text-primary)',
    outline: 'none',
  } as const

  return (
    <div
      style={{
        height: 44,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'var(--color-white)',
        border: `1px solid ${invalide ? 'var(--color-black)' : 'var(--color-border)'}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          backgroundColor: 'var(--color-gray-5)',
          borderRight: '1px solid var(--color-border)',
          fontSize: 'var(--font-size-copy2)',
          fontWeight: 600,
        }}
      >
        {badge}_
      </span>
      <input
        aria-label={`Année de la ${badge}`}
        inputMode="numeric"
        value={valeur.annee}
        disabled={desactive}
        onChange={(e) => onChange({ ...valeur, annee: chiffres(e.target.value, 4) })}
        style={{ ...champ, width: 52, textAlign: 'center' }}
      />
      <span aria-hidden="true" style={{ color: 'var(--color-text-secondary)' }}>-</span>
      <input
        id={id}
        inputMode="numeric"
        placeholder={badge === 'FNC-T' ? '55' : '72'}
        value={valeur.numero}
        disabled={desactive}
        onChange={(e) => onChange({ ...valeur, numero: chiffres(e.target.value, 3) })}
        style={{ ...champ, flexGrow: 1, minWidth: 0, padding: '0 10px' }}
      />
    </div>
  )
}
